import { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ArrowUUpLeft,
    MagnifyingGlass,
    Warning,
    Calculator,
    Barcode,
    Plus,
    Minus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSuccessOverlay } from "@/lib/success-overlay";
import BarcodeScanner from "@/components/BarcodeScanner";
import { useNumpad } from "@/lib/numpad-context";

function currentMonthKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReturnDialog({ open, onClose, onSuccess }) {
    const [step, setStep] = useState("search"); // search | sales | items
    const [code, setCode] = useState("");
    const [customer, setCustomer] = useState(null);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [chosen, setChosen] = useState(null);
    // qtyMap: { item_id: qty_to_return }
    const [qtyMap, setQtyMap] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);

    const numpad = useNumpad();
    const successOverlay = useSuccessOverlay();

    useEffect(() => {
        if (open) {
            setStep("search");
            setCode("");
            setCustomer(null);
            setSales([]);
            setChosen(null);
            setQtyMap({});
            setSubmitting(false);
        }
    }, [open]);

    // Filter to only current-month sales (returns disallowed for previous months)
    const currentMonth = currentMonthKey();
    const eligibleSales = useMemo(
        () => sales.filter((s) => (s.month_key || "") === currentMonth),
        [sales, currentMonth]
    );

    const lookup = async (overrideCode) => {
        const q = (overrideCode ?? code).trim();
        if (!q) {
            toast.error("Εισάγετε κωδικό, ΑΦΜ ή barcode");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(
                `/customers/search/${encodeURIComponent(q)}`
            );
            setCustomer(data);
            const sRes = await api.get(`/customers/${data.id}/sales`);
            setSales(sRes.data || []);
            setStep("sales");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Δεν βρέθηκε πελάτης");
        } finally {
            setLoading(false);
        }
    };

    const onScan = (val) => {
        setScannerOpen(false);
        if (val) {
            setCode(val);
            lookup(val);
        }
    };

    const openNumpad = () => {
        numpad.open({
            value: code,
            label: "Αναζήτηση πελάτη",
            onChange: (v) => setCode(v),
            onApply: (v) => {
                setCode(v);
                lookup(v);
            },
        });
    };

    const pickSale = (s) => {
        setChosen(s);
        // Initialise qtyMap with FULL remaining quantities pre-selected
        const initial = {};
        const ret = s.returned_quantities || {};
        for (const li of s.line_items || []) {
            const remaining = Math.max(
                0,
                Number(li.quantity) - Number(ret[li.item_id] || 0)
            );
            initial[li.item_id] = remaining;
        }
        setQtyMap(initial);
        setStep("items");
    };

    const updateQty = (item_id, max, delta) => {
        setQtyMap((m) => {
            const cur = Number(m[item_id] || 0);
            const next = Math.max(0, Math.min(max, cur + delta));
            return { ...m, [item_id]: next };
        });
    };

    const setQtyDirect = (item_id, max, value) => {
        const v = Math.max(0, Math.min(max, Number(value) || 0));
        setQtyMap((m) => ({ ...m, [item_id]: v }));
    };

    const totalReturn = useMemo(() => {
        if (!chosen) return 0;
        let t = 0;
        for (const li of chosen.line_items || []) {
            const orig = Number(li.quantity);
            const req = Number(qtyMap[li.item_id] || 0);
            const ratio = orig > 0 ? req / orig : 0;
            t += Number(li.line_gross) * ratio;
        }
        return t;
    }, [chosen, qtyMap]);

    const submitReturn = async () => {
        if (!chosen) return;
        const items = Object.entries(qtyMap)
            .filter(([_, q]) => Number(q) > 0)
            .map(([item_id, q]) => ({ item_id, quantity: Number(q) }));
        if (items.length === 0) {
            toast.error("Επιλέξτε τουλάχιστον 1 τεμάχιο προς επιστροφή");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/sales/${chosen.id}/return`, { items });
            toast.success(
                `Επιστροφή ${fmtEUR(Math.abs(data.total_gross))} καταχωρήθηκε`
            );
            successOverlay.show();
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα επιστροφής");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
                <DialogContent
                    className="rounded-none border-2 border-[#8B2530] max-w-4xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
                    data-testid="return-dialog"
                    onInteractOutside={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => {
                        if (submitting) e.preventDefault();
                    }}
                >
                    <DialogHeader className="p-6 border-b-2 border-[#8B2530] bg-[#FDEAE8]">
                        <DialogTitle className="font-heading text-2xl font-black tracking-tighter text-[#8B2530] flex items-center gap-2">
                            <ArrowUUpLeft size={24} weight="bold" />
                            ΕΠΙΣΤΡΟΦΗ ΕΙΔΩΝ
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#5C5F66]">
                            {step === "search" &&
                                "Αναζητήστε πελάτη με κωδικό, ΑΦΜ, σάρωση barcode/QR ή numpad."}
                            {step === "sales" &&
                                "Επιλέξτε πώληση του τρέχοντος μήνα. Μόνο πωλήσεις του μήνα μπορούν να επιστραφούν."}
                            {step === "items" &&
                                "Επιλέξτε ποσότητα ανά είδος για επιστροφή. Το ποσό θα προστεθεί στο υπόλοιπο του πελάτη."}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="p-6 space-y-4">
                        {/* Step 1 — search */}
                        {step === "search" && (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    lookup();
                                }}
                                className="flex gap-2"
                            >
                                <div className="relative flex-1">
                                    <MagnifyingGlass
                                        size={18}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                                    />
                                    <Input
                                        autoFocus
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        placeholder="Κωδικός / ΑΦΜ / Barcode πελάτη"
                                        data-testid="return-customer-input"
                                        className="rounded-none h-12 pl-10 border-[#0A0A0A] text-base"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    onClick={() => setScannerOpen(true)}
                                    title="Σάρωση barcode/QR"
                                    data-testid="return-scan-btn"
                                    className="rounded-none h-12 w-12 p-0 bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                                >
                                    <Barcode size={20} weight="bold" />
                                </Button>
                                <Button
                                    type="button"
                                    onClick={openNumpad}
                                    title="Numpad"
                                    data-testid="return-numpad-btn"
                                    className="rounded-none h-12 w-12 p-0 bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                                >
                                    <Calculator size={20} weight="bold" />
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="return-search-btn"
                                    className="rounded-none h-12 px-6 bg-[#0A0A0A] text-white font-black tracking-wider"
                                >
                                    {loading ? "..." : "ΑΝΑΖΗΤΗΣΗ"}
                                </Button>
                            </form>
                        )}

                        {/* Step 2 — sales list */}
                        {step === "sales" && customer && (
                            <>
                                <div className="bg-[#F4F5F7] p-3 border-2 border-[#0A0A0A]">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                                        ΠΕΛΑΤΗΣ
                                    </div>
                                    <div className="font-heading text-lg font-black tracking-tighter">
                                        #{customer.code} {customer.name}
                                    </div>
                                </div>
                                {eligibleSales.length === 0 ? (
                                    <div className="text-center py-8 text-sm text-[#5C5F66]">
                                        Δεν υπάρχουν πωλήσεις του τρέχοντος μήνα για αυτόν τον πελάτη.
                                    </div>
                                ) : (
                                    <div className="border-2 border-[#0A0A0A] max-h-[400px] overflow-y-auto">
                                        <Table>
                                            <TableHeader className="sticky top-0 bg-[#F4F5F7] z-10">
                                                <TableRow className="border-b-2 border-[#0A0A0A]">
                                                    <TableHead className="text-[10px] font-black uppercase tracking-[0.15em]">
                                                        ΗΜ/ΝΙΑ
                                                    </TableHead>
                                                    <TableHead className="text-[10px] font-black uppercase tracking-[0.15em]">
                                                        ΕΙΔΗ
                                                    </TableHead>
                                                    <TableHead className="text-right text-[10px] font-black uppercase tracking-[0.15em]">
                                                        ΣΥΝΟΛΟ
                                                    </TableHead>
                                                    <TableHead className="text-right text-[10px] font-black uppercase tracking-[0.15em]">
                                                        ΕΝΕΡΓΕΙΑ
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {eligibleSales.map((s) => {
                                                    const isReturn = !!s.is_return;
                                                    const fullyReturned = !!s.returned;
                                                    const partiallyReturned =
                                                        !fullyReturned &&
                                                        Object.values(
                                                            s.returned_quantities || {}
                                                        ).some((v) => Number(v) > 0);
                                                    return (
                                                        <TableRow
                                                            key={s.id}
                                                            data-testid={`return-sale-row-${s.id}`}
                                                            className={
                                                                isReturn
                                                                    ? "bg-[#FDEAE8]"
                                                                    : fullyReturned
                                                                      ? "bg-[#F4F5F7] opacity-60"
                                                                      : ""
                                                            }
                                                        >
                                                            <TableCell className="font-mono text-xs">
                                                                {fmtDate(s.created_at)}
                                                                <div className="text-[9px] text-[#5C5F66] mt-0.5">
                                                                    #{s.id.slice(0, 8)}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-xs">
                                                                {(s.line_items || [])
                                                                    .slice(0, 2)
                                                                    .map(
                                                                        (li) =>
                                                                            `${li.description} x${Math.abs(Number(li.quantity))}`
                                                                    )
                                                                    .join(", ")}
                                                                {(s.line_items || []).length >
                                                                    2 &&
                                                                    ` +${s.line_items.length - 2}`}
                                                                {isReturn && (
                                                                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-black bg-[#8B2530] text-white">
                                                                        ΕΠΙΣΤΡΟΦΗ
                                                                    </span>
                                                                )}
                                                                {fullyReturned && (
                                                                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-black bg-[#FF9500] text-white">
                                                                        ΕΧΕΙ ΕΠΙΣΤΡΑΦΕΙ
                                                                    </span>
                                                                )}
                                                                {partiallyReturned && (
                                                                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-black bg-[#FFCC00] text-[#0A0A0A]">
                                                                        ΜΕΡΙΚΗ ΕΠΙΣΤΡΟΦΗ
                                                                    </span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                className={`text-right font-mono font-bold ${
                                                                    isReturn
                                                                        ? "text-[#8B2530]"
                                                                        : ""
                                                                }`}
                                                            >
                                                                {fmtEUR(s.total_gross)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {!isReturn &&
                                                                    !fullyReturned && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                pickSale(s)
                                                                            }
                                                                            data-testid={`return-pick-${s.id}`}
                                                                            className="rounded-none h-9 px-3 bg-[#8B2530] hover:bg-[#6B1A24] text-white text-xs font-black"
                                                                        >
                                                                            ΕΠΙΛΟΓΗ
                                                                        </Button>
                                                                    )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Step 3 — items qty selection */}
                        {step === "items" && chosen && customer && (
                            <div className="space-y-4">
                                <div className="border-2 border-[#FF9500] bg-[#FFF8E1] p-4 flex gap-3 items-start">
                                    <Warning
                                        size={24}
                                        weight="fill"
                                        className="text-[#FF9500] shrink-0"
                                    />
                                    <div className="flex-1">
                                        <div className="text-xs font-black uppercase tracking-[0.2em] text-[#7A5C00]">
                                            ΘΑ ΓΙΝΕΙ ΕΠΙΣΤΡΟΦΗ ΕΙΔΩΝ
                                        </div>
                                        <div
                                            className="text-sm text-[#0A0A0A] mt-1"
                                            data-testid="return-source-info"
                                        >
                                            Από την παραγγελία{" "}
                                            <strong>#{chosen.id.slice(0, 8)}</strong> της{" "}
                                            <strong>{fmtDate(chosen.created_at)}</strong>{" "}
                                            του πελάτη #{customer.code} {customer.name}.
                                            Το συνολικό ποσό θα προστεθεί στο υπόλοιπο.
                                        </div>
                                    </div>
                                </div>

                                <div className="border-2 border-[#0A0A0A]">
                                    <div className="px-3 py-2 bg-[#F4F5F7] border-b-2 border-[#0A0A0A] text-[10px] font-black uppercase tracking-[0.2em]">
                                        ΕΠΙΛΕΞΤΕ ΠΟΣΟΤΗΤΑ ΑΝΑ ΕΙΔΟΣ
                                    </div>
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-[#F4F5F7] border-b border-[#E2E4E9]">
                                                <TableHead className="text-[10px] font-black uppercase tracking-wider">
                                                    ΕΙΔΟΣ
                                                </TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-center">
                                                    ΑΡΧΙΚΗ
                                                </TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-center">
                                                    ΗΔΗ ΕΠΕΣΤΡΑΨΕ
                                                </TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-center">
                                                    ΤΩΡΑ
                                                </TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-wider text-right">
                                                    ΑΞΙΑ
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(chosen.line_items || []).map((li, idx) => {
                                                const ret = Number(
                                                    (chosen.returned_quantities || {})[
                                                        li.item_id
                                                    ] || 0
                                                );
                                                const remaining =
                                                    Number(li.quantity) - ret;
                                                const cur = Number(
                                                    qtyMap[li.item_id] || 0
                                                );
                                                const lineValue =
                                                    remaining > 0
                                                        ? (Number(li.line_gross) /
                                                              Number(li.quantity)) *
                                                          cur
                                                        : 0;
                                                const disabled = remaining <= 0;
                                                return (
                                                    <TableRow
                                                        key={idx}
                                                        data-testid={`return-line-${li.item_id}`}
                                                    >
                                                        <TableCell className="font-semibold">
                                                            {li.description}
                                                            <div className="text-[10px] text-[#5C5F66]">
                                                                {fmtEUR(li.unit_price)} /
                                                                τεμ
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center font-mono">
                                                            {Number(li.quantity)}
                                                        </TableCell>
                                                        <TableCell className="text-center font-mono text-[#5C5F66]">
                                                            {ret > 0 ? ret : "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center justify-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        updateQty(
                                                                            li.item_id,
                                                                            remaining,
                                                                            -1
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        disabled || cur <= 0
                                                                    }
                                                                    data-testid={`return-qty-minus-${li.item_id}`}
                                                                    className="w-8 h-8 bg-[#0A0A0A] text-white disabled:opacity-30 flex items-center justify-center"
                                                                >
                                                                    <Minus
                                                                        size={14}
                                                                        weight="bold"
                                                                    />
                                                                </button>
                                                                <Input
                                                                    type="number"
                                                                    min={0}
                                                                    max={remaining}
                                                                    value={cur}
                                                                    onChange={(e) =>
                                                                        setQtyDirect(
                                                                            li.item_id,
                                                                            remaining,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    disabled={disabled}
                                                                    data-testid={`return-qty-input-${li.item_id}`}
                                                                    className="rounded-none h-8 w-14 text-center font-mono p-0 border-2 border-[#0A0A0A]"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        updateQty(
                                                                            li.item_id,
                                                                            remaining,
                                                                            1
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        disabled ||
                                                                        cur >= remaining
                                                                    }
                                                                    data-testid={`return-qty-plus-${li.item_id}`}
                                                                    className="w-8 h-8 bg-[#0A0A0A] text-white disabled:opacity-30 flex items-center justify-center"
                                                                >
                                                                    <Plus
                                                                        size={14}
                                                                        weight="bold"
                                                                    />
                                                                </button>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono font-bold">
                                                            {fmtEUR(lineValue)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            <TableRow className="bg-[#0A0A0A]">
                                                <TableCell
                                                    colSpan={4}
                                                    className="text-white font-black tracking-wider text-sm"
                                                >
                                                    ΣΥΝΟΛΟ ΕΠΙΣΤΡΟΦΗΣ
                                                </TableCell>
                                                <TableCell
                                                    className="text-right text-white font-mono font-black text-base"
                                                    data-testid="return-total"
                                                >
                                                    {fmtEUR(totalReturn)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="p-6 border-t border-[#E2E4E9] gap-2">
                        {step === "sales" && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setStep("search")}
                                data-testid="return-back-search"
                                className="rounded-none h-12 px-5 border-2 border-[#0A0A0A] font-bold"
                            >
                                ΑΛΛΑΓΗ ΠΕΛΑΤΗ
                            </Button>
                        )}
                        {step === "items" && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setChosen(null);
                                    setQtyMap({});
                                    setStep("sales");
                                }}
                                data-testid="return-back-list"
                                className="rounded-none h-12 px-5 border-2 border-[#0A0A0A] font-bold"
                            >
                                ΠΙΣΩ
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={submitting}
                            data-testid="return-cancel-btn"
                            className="rounded-none h-12 px-5 border-2 border-[#0A0A0A] font-bold"
                        >
                            ΑΚΥΡΟ
                        </Button>
                        {step === "items" && (
                            <Button
                                type="button"
                                onClick={submitReturn}
                                disabled={submitting || totalReturn === 0}
                                data-testid="return-confirm-btn"
                                className="rounded-none h-12 px-6 bg-[#8B2530] hover:bg-[#6B1A24] text-white font-black tracking-wider disabled:opacity-50"
                            >
                                {submitting
                                    ? "ΚΑΤΑΧΩΡΗΣΗ..."
                                    : "ΟΛΟΚΛΗΡΩΣΗ ΕΠΙΣΤΡΟΦΗΣ"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <BarcodeScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={onScan}
            />
        </>
    );
}
